import React, { useState, useEffect, useRef, useCallback } from 'react';
import { loteService } from '../services/api';
import { toast } from 'react-toastify';
import './PlanoInteractivo.css';

import { planoService } from '../services/planoService';
import ConfirmModal from './ConfirmModal';

// Colores por estado (constante fuera del componente)
const coloresEstado = {
  disponible: 'rgba(17, 153, 142, 0.6)', // Verde
  reservado: 'rgba(245, 87, 108, 0.6)', // Rosa/Rojo
  vendido: 'rgba(79, 172, 254, 0.6)', // Azul
};

/**
 * Componente PlanoInteractivo
 * Muestra el plano con lotes interactivos usando Canvas
 * Los lotes se colorean según su estado
 */
const PlanoInteractivo = ({ onLoteClick, planoImage = '/plano.jpg', onLoteActualizado, onPlanoChange }) => {
  const canvasRef = useRef(null);
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [scale, setScale] = useState(1);
  const imageRef = useRef(null);

  // Planos y selección
  const [planos, setPlanos] = useState([]);
  const [currentPlano, setCurrentPlano] = useState(null); // objeto plano activo
  const [currentPlanoUrl, setCurrentPlanoUrl] = useState(planoImage);
  const fileInputRef = useRef(null);

  // Guardar planoImage en ref para evitar cambios en dependency array
  const planoImageRef = useRef(planoImage);

  // Modal para eliminar imagen
  const [imageConfirm, setImageConfirm] = useState({ visible: false, plano: null });

  const requestDeleteImage = (plano) => setImageConfirm({ visible: true, plano });
  const cancelDeleteImage = () => setImageConfirm({ visible: false, plano: null });
  const confirmDeleteImage = async () => {
    const p = imageConfirm.plano;
    try {
      await planoService.deletePlano(p._id);
      setPlanos((prev) => prev.filter((x) => x._id !== p._id));
      if (p.active) setCurrentPlanoUrl(planoImage);
      toast.success('Plano eliminado');
    } catch (err) {
      toast.error('Error al eliminar plano');
    } finally {
      setImageConfirm({ visible: false, plano: null });
    }
  };


  // Estados para el arrastre
  const [loteArrastrando, setLoteArrastrando] = useState(null);
  const [offsetArrastre, setOffsetArrastre] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const lotesRef = useRef(lotes); // Ref para acceder al estado más reciente

  // Estados para edición avanzada
  const [loteEditando, setLoteEditando] = useState(null); // Lote en modo edición
  const [puntoEditando, setPuntoEditando] = useState(null); // Punto de polígono siendo editado
  const [handleEditando, setHandleEditando] = useState(null); // Handle de redimensionamiento
  const [modoCreacion, setModoCreacion] = useState(null); // 'rectangulo', 'circulo', 'poligono'
  const [geometriaPreview, setGeometriaPreview] = useState(null); // Geometría de vista previa
  const [puntosPoligono, setPuntosPoligono] = useState([]); // Puntos del polígono en creación



  // Cargar lotes cada vez que cambia el plano activo
  useEffect(() => {
    lotesRef.current = lotes;
  }, [lotes]);

  // Cargar planos del usuario - SOLO AL MONTAR
  useEffect(() => {
    const cargarPlanos = async () => {
      try {
        const data = await planoService.getPlanos();
        setPlanos(data);
        const activo = data.find((p) => p.active);
        if (activo) {
          setCurrentPlano(activo);
          setCurrentPlanoUrl(activo.url);
          if (typeof onPlanoChange === 'function') onPlanoChange(activo);
        } else {
          setCurrentPlano(null);
          setCurrentPlanoUrl(planoImageRef.current);
          if (typeof onPlanoChange === 'function') onPlanoChange(null);
        }
      } catch (err) {
        console.error('Error al cargar planos:', err);
      }
    };

    cargarPlanos();
  }, [onPlanoChange]);

  // Cargar lotes cuando cambia el plano actual
  useEffect(() => {
    const cargarLotes = async () => {
      try {
        setLoading(true);
        const filters = {};
        if (currentPlano && currentPlano._id) filters.plano = currentPlano._id;
        const data = await loteService.obtenerLotes(filters);
        setLotes(data);
      } catch (error) {
        console.error('Error al cargar lotes:', error);
      } finally {
        setLoading(false);
      }
    };

    cargarLotes();
  }, [currentPlano]);

  // Función para dibujar un rectángulo
  const dibujarRectangulo = (ctx, lote, color, esArrastrando, scale) => {
    const { x, y, width, height } = lote.geometria || lote.coordenadas;
    ctx.fillStyle = esArrastrando ? color.replace('0.6', '0.8') : color;
    ctx.fillRect(x * scale, y * scale, width * scale, height * scale);
    ctx.strokeStyle = esArrastrando ? '#ff6b6b' : '#333';
    ctx.lineWidth = esArrastrando ? 3 : 2;
    ctx.strokeRect(x * scale, y * scale, width * scale, height * scale);
    return { x: (x + width / 2) * scale, y: (y + height / 2) * scale };
  };

  // Función para dibujar un círculo
  const dibujarCirculo = (ctx, lote, color, esArrastrando, scale) => {
    const { x, y, radius } = lote.geometria || { x: 0, y: 0, radius: 50 };
    ctx.beginPath();
    ctx.arc(x * scale, y * scale, radius * scale, 0, 2 * Math.PI);
    ctx.fillStyle = esArrastrando ? color.replace('0.6', '0.8') : color;
    ctx.fill();
    ctx.strokeStyle = esArrastrando ? '#ff6b6b' : '#333';
    ctx.lineWidth = esArrastrando ? 3 : 2;
    ctx.stroke();
    return { x: x * scale, y: y * scale };
  };

  // Función para dibujar un polígono
  const dibujarPoligono = (ctx, lote, color, esArrastrando, scale) => {
    const puntos = lote.geometria?.puntos || [];
    if (puntos.length < 3) return null;

    ctx.beginPath();
    puntos.forEach((punto, index) => {
      if (index === 0) {
        ctx.moveTo(punto.x * scale, punto.y * scale);
      } else {
        ctx.lineTo(punto.x * scale, punto.y * scale);
      }
    });
    ctx.closePath();
    ctx.fillStyle = esArrastrando ? color.replace('0.6', '0.8') : color;
    ctx.fill();
    ctx.strokeStyle = esArrastrando ? '#ff6b6b' : '#333';
    ctx.lineWidth = esArrastrando ? 3 : 2;
    ctx.stroke();

    // Calcular centro para el texto
    const centroX = puntos.reduce((sum, p) => sum + p.x, 0) / puntos.length;
    const centroY = puntos.reduce((sum, p) => sum + p.y, 0) / puntos.length;
    return { x: centroX * scale, y: centroY * scale };
  };

  // Dibujar handles de redimensionamiento
  const dibujarHandles = (ctx, lote, scale) => {
    const tipoForma = lote.tipoForma || 'rectangulo';
    const handleSize = 8 / scale;

    if (tipoForma === 'rectangulo') {
      const { x, y, width, height } = lote.geometria || lote.coordenadas;
      const handles = [
        { x: x, y: y }, // Esquina superior izquierda
        { x: x + width, y: y }, // Esquina superior derecha
        { x: x + width, y: y + height }, // Esquina inferior derecha
        { x: x, y: y + height }, // Esquina inferior izquierda
        { x: x + width / 2, y: y }, // Centro superior
        { x: x + width, y: y + height / 2 }, // Centro derecha
        { x: x + width / 2, y: y + height }, // Centro inferior
        { x: x, y: y + height / 2 }, // Centro izquierda
      ];

      handles.forEach((handle, index) => {
        ctx.fillStyle = '#ff6b6b';
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(handle.x * scale, handle.y * scale, handleSize * scale, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
      });
    } else if (tipoForma === 'circulo') {
      const { x, y, radius } = lote.geometria || { x: 0, y: 0, radius: 50 };
      // Handle en el borde derecho del círculo
      ctx.fillStyle = '#ff6b6b';
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc((x + radius) * scale, y * scale, handleSize * scale, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    }
  };

  // Dibujar puntos editables de polígono
  const dibujarPuntosPoligono = (ctx, lote, scale, puntoEditandoActual) => {
    const puntos = lote.geometria?.puntos || [];
    const handleSize = 6 / scale;

    puntos.forEach((punto, index) => {
      const esEditando = puntoEditandoActual === index;
      ctx.fillStyle = esEditando ? '#ff6b6b' : '#4facfe';
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(punto.x * scale, punto.y * scale, handleSize * scale, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();

      // Número del punto
      ctx.fillStyle = '#fff';
      ctx.font = `${10 * scale}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(
        (index + 1).toString(),
        punto.x * scale,
        punto.y * scale - (handleSize + 5) * scale
      );

      // Botón de eliminar (pequeño X)
      if (puntos.length > 3) {
        ctx.fillStyle = '#f5576c';
        ctx.font = `${8 * scale}px Arial`;
        ctx.fillText(
          '×',
          punto.x * scale + handleSize * scale,
          punto.y * scale - handleSize * scale
        );
      }
    });
  };

  const dibujarPlano = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    if (!img || !img.complete) return;

    // Ajustar tamaño del canvas (buffer) y asegurar que el tamaño visual coincida
    canvas.width = img.width * scale;
    canvas.height = img.height * scale;
    // Forzar que el elemento canvas muestre el mismo tamaño que su buffer para evitar desalineación
    canvas.style.width = `${canvas.width}px`;
    canvas.style.height = `${canvas.height}px`;

    // Limpiar canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Dibujar imagen de fondo
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Dibujar lotes
    lotesRef.current.forEach((lote) => {
      const tipoForma = lote.tipoForma || 'rectangulo';
      const color = coloresEstado[lote.estado] || coloresEstado.disponible;
      const esLoteArrastrando = loteArrastrando && loteArrastrando._id === lote._id;
      const esEditando = loteEditando && loteEditando._id === lote._id;

      let centro = null;

      // Dibujar según el tipo de forma
      if (tipoForma === 'rectangulo') {
        centro = dibujarRectangulo(ctx, lote, color, esLoteArrastrando || esEditando, scale);
      } else if (tipoForma === 'circulo') {
        centro = dibujarCirculo(ctx, lote, color, esLoteArrastrando || esEditando, scale);
      } else if (tipoForma === 'poligono') {
        centro = dibujarPoligono(ctx, lote, color, esLoteArrastrando || esEditando, scale);
      }

      // Dibujar número del lote en el centro
      if (centro) {
        ctx.fillStyle = '#fff';
        ctx.font = `${14 * scale}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(lote.numero, centro.x, centro.y);
      }

      // Dibujar handles de edición
      if (esEditando) {
        if (tipoForma === 'poligono') {
          dibujarPuntosPoligono(ctx, lote, scale, puntoEditando);
        } else {
          dibujarHandles(ctx, lote, scale);
        }
      }
    });

    // Dibujar vista previa de creación
    if (modoCreacion && geometriaPreview) {
      ctx.globalAlpha = 0.5;
      if (modoCreacion === 'rectangulo') {
        const { x, y, width, height } = geometriaPreview;
        ctx.fillStyle = '#4facfe';
        ctx.fillRect(x * scale, y * scale, width * scale, height * scale);
        ctx.strokeStyle = '#1976d2';
        ctx.lineWidth = 2;
        ctx.strokeRect(x * scale, y * scale, width * scale, height * scale);
      } else if (modoCreacion === 'circulo') {
        const { x, y, radius } = geometriaPreview;
        ctx.beginPath();
        ctx.arc(x * scale, y * scale, radius * scale, 0, 2 * Math.PI);
        ctx.fillStyle = '#4facfe';
        ctx.fill();
        ctx.strokeStyle = '#1976d2';
        ctx.lineWidth = 2;
        ctx.stroke();
      } else if (modoCreacion === 'poligono' && puntosPoligono.length > 0) {
        ctx.beginPath();
        puntosPoligono.forEach((punto, index) => {
          if (index === 0) {
            ctx.moveTo(punto.x * scale, punto.y * scale);
          } else {
            ctx.lineTo(punto.x * scale, punto.y * scale);
          }
        });
        if (puntosPoligono.length > 2) {
          ctx.closePath();
          ctx.fillStyle = '#4facfe';
          ctx.fill();
        }
        ctx.strokeStyle = '#1976d2';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Dibujar puntos del polígono
        puntosPoligono.forEach((punto) => {
          ctx.fillStyle = '#4facfe';
          ctx.beginPath();
          ctx.arc(punto.x * scale, punto.y * scale, 5 * scale, 0, 2 * Math.PI);
          ctx.fill();
        });
      }
      ctx.globalAlpha = 1.0;
    }
  }, [scale, loteArrastrando, loteEditando, puntoEditando, modoCreacion, geometriaPreview, puntosPoligono]);

  useEffect(() => {
    if (imageLoaded) {
      dibujarPlano();
    }
  }, [lotes, imageLoaded, dibujarPlano]);

  // Redibujar cuando cambian estados de interacción
  useEffect(() => {
    if (imageLoaded) {
      dibujarPlano();
    }
  }, [loteArrastrando, loteEditando, puntoEditando, modoCreacion, geometriaPreview, puntosPoligono, scale, imageLoaded, dibujarPlano]);

  // Helper: convertir coordenadas del cliente (eventos) a coordenadas en la imagen original
  const clientToImageCoords = (clientX, clientY) => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas) return { x: clientX, y: clientY };

    const rect = canvas.getBoundingClientRect();

    // Si tenemos la imagen y sus dimensiones, mapear del tamaño visual al tamaño real
    if (img && rect.width > 0 && rect.height > 0) {
      const x = (clientX - rect.left) * (img.width / rect.width);
      const y = (clientY - rect.top) * (img.height / rect.height);
      return { x, y };
    }

    // Fallback (uso antiguo /scale)
    return { x: (clientX - rect.left) / scale, y: (clientY - rect.top) / scale };
  };

  // Función para verificar si un punto está dentro de un rectángulo
  const puntoEnRectangulo = (px, py, lote) => {
    const geom = lote.geometria || lote.coordenadas;
    const { x, y, width, height } = geom;
    return px >= x && px <= x + width && py >= y && py <= y + height;
  };

  // Función para verificar si un punto está dentro de un círculo
  const puntoEnCirculo = (px, py, lote) => {
    const { x, y, radius } = lote.geometria || { x: 0, y: 0, radius: 50 };
    const distancia = Math.sqrt(Math.pow(px - x, 2) + Math.pow(py - y, 2));
    return distancia <= radius;
  };

  // Función para verificar si un punto está dentro de un polígono (ray casting algorithm)
  const puntoEnPoligono = (px, py, lote) => {
    const puntos = lote.geometria?.puntos || [];
    if (puntos.length < 3) return false;

    let dentro = false;
    for (let i = 0, j = puntos.length - 1; i < puntos.length; j = i++) {
      const xi = puntos[i].x,
        yi = puntos[i].y;
      const xj = puntos[j].x,
        yj = puntos[j].y;

      const intersecta = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
      if (intersecta) dentro = !dentro;
    }
    return dentro;
  };

  const obtenerLoteEnPosicion = (x, y) => {
    return lotesRef.current.find((lote) => {
      const tipoForma = lote.tipoForma || 'rectangulo';

      if (tipoForma === 'rectangulo') {
        return puntoEnRectangulo(x, y, lote);
      } else if (tipoForma === 'circulo') {
        return puntoEnCirculo(x, y, lote);
      } else if (tipoForma === 'poligono') {
        return puntoEnPoligono(x, y, lote);
      }
      return false;
    });
  };

  // Verificar si se clickeó un handle de redimensionamiento
  const obtenerHandleClickeado = (x, y, lote) => {
    const tipoForma = lote.tipoForma || 'rectangulo';
    const handleSize = 8;

    if (tipoForma === 'rectangulo') {
      const { x: lx, y: ly, width, height } = lote.geometria || lote.coordenadas;
      const handles = [
        { x: lx, y: ly, tipo: 'nw' }, // Noroeste
        { x: lx + width, y: ly, tipo: 'ne' }, // Noreste
        { x: lx + width, y: ly + height, tipo: 'se' }, // Sureste
        { x: lx, y: ly + height, tipo: 'sw' }, // Suroeste
        { x: lx + width / 2, y: ly, tipo: 'n' }, // Norte
        { x: lx + width, y: ly + height / 2, tipo: 'e' }, // Este
        { x: lx + width / 2, y: ly + height, tipo: 's' }, // Sur
        { x: lx, y: ly + height / 2, tipo: 'w' }, // Oeste
      ];

      for (const handle of handles) {
        const distancia = Math.sqrt(Math.pow(x - handle.x, 2) + Math.pow(y - handle.y, 2));
        if (distancia <= handleSize) {
          return handle.tipo;
        }
      }
    } else if (tipoForma === 'circulo') {
      const { x: cx, y: cy, radius } = lote.geometria || { x: 0, y: 0, radius: 50 };
      const handleX = cx + radius;
      const distancia = Math.sqrt(Math.pow(x - handleX, 2) + Math.pow(y - cy, 2));
      if (distancia <= handleSize) {
        return 'radius';
      }
    }

    return null;
  };

  // Verificar si se clickeó un punto de polígono
  const obtenerPuntoPoligonoClickeado = (x, y, lote) => {
    const puntos = lote.geometria?.puntos || [];
    const handleSize = 6;

    for (let i = 0; i < puntos.length; i++) {
      const punto = puntos[i];
      const distancia = Math.sqrt(Math.pow(x - punto.x, 2) + Math.pow(y - punto.y, 2));
      if (distancia <= handleSize) {
        return i;
      }
    }

    return null;
  };

  const handleCanvasMouseDown = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = clientToImageCoords(e.clientX, e.clientY);

    // Modo creación
    if (modoCreacion) {
      if (modoCreacion === 'poligono') {
        // Agregar punto al polígono
        setPuntosPoligono([...puntosPoligono, { x, y }]);
      } else if (modoCreacion === 'rectangulo' && !geometriaPreview) {
        // Iniciar rectángulo
        setGeometriaPreview({ x, y, width: 0, height: 0, startX: x, startY: y });
      } else if (modoCreacion === 'circulo' && !geometriaPreview) {
        // Iniciar círculo
        setGeometriaPreview({ x, y, radius: 0, startX: x, startY: y });
      }
      return;
    }

    // Modo edición
    if (loteEditando) {
      const handle = obtenerHandleClickeado(x, y, loteEditando);
      if (handle) {
        setHandleEditando(handle);
        setIsDragging(true);
        canvas.style.cursor = 'crosshair';
        return;
      }

      // Verificar si se clickeó un punto de polígono
      if (loteEditando.tipoForma === 'poligono') {
        const puntoIndex = obtenerPuntoPoligonoClickeado(x, y, loteEditando);
        if (puntoIndex !== null) {
          // Verificar si se clickeó en el botón de eliminar (área superior derecha del punto)
          const punto = loteEditando.geometria?.puntos[puntoIndex];
          if (punto) {
            const distanciaX = Math.abs(x - punto.x - 6);
            const distanciaY = Math.abs(y - punto.y + 6);
            if (distanciaX < 5 && distanciaY < 5 && loteEditando.geometria?.puntos.length > 3) {
              // Eliminar punto
              eliminarPuntoPoligono(loteEditando, puntoIndex);
              return;
            }
          }
          setPuntoEditando(puntoIndex);
          setIsDragging(true);
          canvas.style.cursor = 'move';
          return;
        }
      }
    }

    // Arrastre normal de lote
    const loteClickeado = obtenerLoteEnPosicion(x, y);

    if (loteClickeado) {
      const tipoForma = loteClickeado.tipoForma || 'rectangulo';
      let offsetX = 0,
        offsetY = 0;

      if (tipoForma === 'rectangulo') {
        const geom = loteClickeado.geometria || loteClickeado.coordenadas;
        offsetX = x - geom.x;
        offsetY = y - geom.y;
      } else if (tipoForma === 'circulo') {
        const { x: cx, y: cy } = loteClickeado.geometria || { x: 0, y: 0 };
        offsetX = x - cx;
        offsetY = y - cy;
      } else if (tipoForma === 'poligono') {
        const puntos = loteClickeado.geometria?.puntos || [];
        if (puntos.length > 0) {
          offsetX = x - puntos[0].x;
          offsetY = y - puntos[0].y;
        }
      }

      setLoteArrastrando(loteClickeado);
      setOffsetArrastre({ x: offsetX, y: offsetY });
      setIsDragging(true);
      canvas.style.cursor = 'grabbing';
    }
  };

  const handleCanvasMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x: mouseX, y: mouseY } = clientToImageCoords(e.clientX, e.clientY);

    // Modo creación - actualizar vista previa
    if (modoCreacion && geometriaPreview) {
      if (modoCreacion === 'rectangulo') {
        const { startX, startY } = geometriaPreview;
        setGeometriaPreview({
          ...geometriaPreview,
          x: Math.min(startX, mouseX),
          y: Math.min(startY, mouseY),
          width: Math.abs(mouseX - startX),
          height: Math.abs(mouseY - startY),
        });
      } else if (modoCreacion === 'circulo') {
        const { startX, startY } = geometriaPreview;
        const radius = Math.sqrt(Math.pow(mouseX - startX, 2) + Math.pow(mouseY - startY, 2));
        setGeometriaPreview({
          ...geometriaPreview,
          x: startX,
          y: startY,
          radius: radius,
        });
      }
      return;
    }

    // Modo edición - redimensionar
    if (isDragging && loteEditando && handleEditando) {
      setLotes((prevLotes) =>
        prevLotes.map((lote) => {
          if (lote._id !== loteEditando._id) return lote;

          const tipoForma = lote.tipoForma || 'rectangulo';
          let nuevaGeometria = { ...lote.geometria };

          if (tipoForma === 'rectangulo') {
            const { x, y, width, height } = nuevaGeometria;
            switch (handleEditando) {
              case 'nw':
                nuevaGeometria = {
                  x: mouseX,
                  y: mouseY,
                  width: width + x - mouseX,
                  height: height + y - mouseY,
                };
                break;
              case 'ne':
                nuevaGeometria = { x, y: mouseY, width: mouseX - x, height: height + y - mouseY };
                break;
              case 'se':
                nuevaGeometria = { x, y, width: mouseX - x, height: mouseY - y };
                break;
              case 'sw':
                nuevaGeometria = { x: mouseX, y, width: width + x - mouseX, height: mouseY - y };
                break;
              case 'n':
                nuevaGeometria = { x, y: mouseY, width, height: height + y - mouseY };
                break;
              case 'e':
                nuevaGeometria = { x, y, width: mouseX - x, height };
                break;
              case 's':
                nuevaGeometria = { x, y, width, height: mouseY - y };
                break;
              case 'w':
                nuevaGeometria = { x: mouseX, y, width: width + x - mouseX, height };
                break;
              default:
                break;
            }
            // Asegurar valores mínimos
            if (nuevaGeometria.width < 10) nuevaGeometria.width = 10;
            if (nuevaGeometria.height < 10) nuevaGeometria.height = 10;
          } else if (tipoForma === 'circulo' && handleEditando === 'radius') {
            const { x, y } = nuevaGeometria;
            const radius = Math.sqrt(Math.pow(mouseX - x, 2) + Math.pow(mouseY - y, 2));
            nuevaGeometria = { ...nuevaGeometria, radius: Math.max(10, radius) };
          }

          return {
            ...lote,
            geometria: nuevaGeometria,
            coordenadas: tipoForma === 'rectangulo' ? nuevaGeometria : lote.coordenadas,
          };
        })
      );
      return;
    }

    // Modo edición - mover punto de polígono
    if (isDragging && loteEditando && puntoEditando !== null) {
      setLotes((prevLotes) =>
        prevLotes.map((lote) => {
          if (lote._id !== loteEditando._id) return lote;

          const puntos = [...(lote.geometria?.puntos || [])];
          puntos[puntoEditando] = { x: Math.max(0, mouseX), y: Math.max(0, mouseY) };

          return {
            ...lote,
            geometria: { ...lote.geometria, puntos },
          };
        })
      );
      return;
    }

    // Arrastre normal de lote
    if (!isDragging || !loteArrastrando) return;

    // Actualizar geometría según el tipo de forma
    setLotes((prevLotes) =>
      prevLotes.map((lote) => {
        if (lote._id !== loteArrastrando._id) return lote;

        const tipoForma = lote.tipoForma || 'rectangulo';
        let nuevaGeometria = { ...lote.geometria };

        if (tipoForma === 'rectangulo') {
          nuevaGeometria = {
            ...nuevaGeometria,
            x: Math.max(0, mouseX - offsetArrastre.x),
            y: Math.max(0, mouseY - offsetArrastre.y),
          };
        } else if (tipoForma === 'circulo') {
          nuevaGeometria = {
            ...nuevaGeometria,
            x: Math.max(0, mouseX),
            y: Math.max(0, mouseY),
          };
        } else if (tipoForma === 'poligono') {
          // Mover todos los puntos del polígono
          const puntosOriginales = lote.geometria?.puntos || [];
          if (puntosOriginales.length > 0) {
            const primerPuntoOriginal = puntosOriginales[0];
            const deltaX = mouseX - primerPuntoOriginal.x - offsetArrastre.x;
            const deltaY = mouseY - primerPuntoOriginal.y - offsetArrastre.y;
            nuevaGeometria = {
              ...nuevaGeometria,
              puntos: puntosOriginales.map((p) => ({
                x: Math.max(0, p.x + deltaX),
                y: Math.max(0, p.y + deltaY),
              })),
            };
          }
        }

        return {
          ...lote,
          geometria: nuevaGeometria,
          // Actualizar coordenadas para compatibilidad
          coordenadas: tipoForma === 'rectangulo' ? nuevaGeometria : lote.coordenadas,
        };
      })
    );
  };

  const handleCanvasMouseUp = async () => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.style.cursor = 'pointer';
    }

    // Guardar cambios de edición
    if (isDragging && (handleEditando || puntoEditando !== null)) {
      const loteActualizado = lotesRef.current.find((l) => l._id === loteEditando._id);
      if (loteActualizado) {
        await guardarCambiosLote(loteActualizado);
      }
      setHandleEditando(null);
      setPuntoEditando(null);
      setIsDragging(false);
      return;
    }

    // Finalizar creación de polígono con doble clic o botón
    if (modoCreacion === 'poligono' && puntosPoligono.length >= 3) {
      // Se finaliza con botón, no aquí
      return;
    }

    // Guardar arrastre normal
    if (!isDragging || !loteArrastrando) return;

    // Obtener el lote actualizado del estado más reciente usando el ref
    const loteActualizado = lotesRef.current.find((l) => l._id === loteArrastrando._id);

    if (loteActualizado) {
      await guardarCambiosLote(loteActualizado);
    }

    // Limpiar estado de arrastre
    setLoteArrastrando(null);
    setOffsetArrastre({ x: 0, y: 0 });
    setIsDragging(false);
  };

  const guardarCambiosLote = async (loteActualizado) => {
    try {
      await loteService.actualizarLote(loteActualizado._id, {
        geometria: loteActualizado.geometria,
        tipoForma: loteActualizado.tipoForma || 'rectangulo',
        coordenadas:
          loteActualizado.tipoForma === 'rectangulo'
            ? loteActualizado.geometria
            : loteActualizado.coordenadas,
      });

      if (onLoteActualizado) {
        onLoteActualizado(loteActualizado);
      }

      // NO recargar lotes - actualizar el estado local directamente
      setLotes((prevLotes) =>
        prevLotes.map((l) => (l._id === loteActualizado._id ? loteActualizado : l))
      );
    } catch (error) {
      console.error('Error al actualizar lote:', error);
      toast.error('Error al actualizar el lote: ' + (error.message || 'Error desconocido'));
      // Si hay error, recargar para sincronizar
      const filters = {};
      if (currentPlano && currentPlano._id) filters.plano = currentPlano._id;
      const data = await loteService.obtenerLotes(filters);
      setLotes(data);
    }
  };

  // Funciones para manejar edición
  const activarModoEdicion = (lote) => {
    setLoteEditando(lote);
    setLoteArrastrando(null);
  };

  const desactivarModoEdicion = () => {
    setLoteEditando(null);
    setPuntoEditando(null);
    setHandleEditando(null);
  };

  // Funciones para polígonos
  const agregarPuntoPoligono = (lote, x, y) => {
    if (!lote || lote.tipoForma !== 'poligono') return;

    setLotes((prevLotes) =>
      prevLotes.map((l) => {
        if (l._id !== lote._id) return l;
        const puntos = [...(l.geometria?.puntos || [])];
        puntos.push({ x, y });
        return {
          ...l,
          geometria: { ...l.geometria, puntos },
        };
      })
    );
  };

  const eliminarPuntoPoligono = async (lote, puntoIndex) => {
    if (!lote || lote.tipoForma !== 'poligono') return;
    const puntos = lote.geometria?.puntos || [];
    if (puntos.length <= 3) {
      toast.error('Un polígono debe tener al menos 3 puntos');
      return;
    }

    const nuevosPuntos = puntos.filter((_, index) => index !== puntoIndex);
    const loteActualizado = {
      ...lote,
      geometria: { ...lote.geometria, puntos: nuevosPuntos },
    };

    setLotes((prevLotes) => prevLotes.map((l) => (l._id === lote._id ? loteActualizado : l)));

    await guardarCambiosLote(loteActualizado);
  };

  const handleCanvasClick = (e) => {
    // Solo procesar clic si no se estaba arrastrando
    if (isDragging) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = clientToImageCoords(e.clientX, e.clientY);

    // Si está en modo creación de polígono, agregar punto
    if (modoCreacion === 'poligono') {
      setPuntosPoligono([...puntosPoligono, { x, y }]);
      return;
    }

    const loteClickeado = obtenerLoteEnPosicion(x, y);

    if (loteClickeado && onLoteClick) {
      onLoteClick(loteClickeado);
    }
  };

  // Finalizar creación de lote
  const finalizarCreacion = async () => {
    if (!modoCreacion || (!geometriaPreview && puntosPoligono.length < 3)) return;

    // Abrir formulario con la geometría predefinida
    const nuevoLote = {
      tipoForma: modoCreacion,
      geometria: modoCreacion === 'poligono' ? { puntos: puntosPoligono } : geometriaPreview,
      coordenadas:
        modoCreacion === 'rectangulo' ? geometriaPreview : { x: 0, y: 0, width: 100, height: 100 },
    };

    if (onLoteClick) {
      onLoteClick(nuevoLote);
    }

    // Limpiar modo creación
    setModoCreacion(null);
    setGeometriaPreview(null);
    setPuntosPoligono([]);
  };

  const cancelarCreacion = () => {
    setModoCreacion(null);
    setGeometriaPreview(null);
    setPuntosPoligono([]);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    if (imageRef.current) {
      // Ajustar escala inicial si la imagen es muy grande
      const maxWidth = window.innerWidth - 100;
      if (imageRef.current.width > maxWidth) {
        setScale(maxWidth / imageRef.current.width);
      }
    }
  };

  if (loading) {
    return <div className="plano-loading">Cargando plano...</div>;
  }

  return (
    <div className="plano-container">
      <div className="plano-controls">
        <h3>Plano Interactivo</h3>
        <div className="plano-leyenda">
          <div className="leyenda-item">
            <span className="leyenda-color disponible"></span>
            <span>Disponible</span>
          </div>
          <div className="leyenda-item">
            <span className="leyenda-color reservado"></span>
            <span>Reservado</span>
          </div>
          <div className="leyenda-item">
            <span className="leyenda-color vendido"></span>
            <span>Vendido</span>
          </div>
        </div>

        {/* Controles de creación */}

        {/* Controles de planos (subida / selección) */}
        <div className="plano-imagenes">
          <h4>Mis planos</h4>
          <div className="plano-upload">
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                try {
                  const nuevo = await planoService.uploadPlano(file);
                  setPlanos((prev) => [nuevo, ...prev]);
                  // establecer como activo y usar URL devuelta
                  const activated = await planoService.activatePlano(nuevo._id);
                  setPlanos((prev) => prev.map((x) => ({ ...x, active: x._id === activated._id })));
                  setImageLoaded(false);
                  setCurrentPlanoUrl(activated.url);
                  toast.success('Plano subido y activado');
                } catch (err) {
                  console.error(err);
                  toast.error(err?.response?.data?.error || 'Error al subir plano');
                } finally {
                  e.target.value = '';
                }
              }}
            />
            <button onClick={() => fileInputRef.current && fileInputRef.current.click()} className="btn-archivo">
              📷 Subir imagen
            </button>
          </div>

          <div className="planos-grid">
            {planos.length === 0 ? (
              <div className="sin-planos">No tienes planos subidos</div>
            ) : (
              planos.map((p) => (
                <div key={p._id} className={`plano-thumb ${p.active ? 'activo' : ''}`}>
                  <img src={p.url} alt={`Plano ${p._id}`} onClick={async () => {
                    try {
                      const activated = await planoService.activatePlano(p._id);
                      setPlanos((prev) => prev.map((x) => ({ ...x, active: x._id === activated._id })));
                      setImageLoaded(false);
                      setCurrentPlano(activated);
                      setCurrentPlanoUrl(activated.url);
                      if (typeof onPlanoChange === 'function') onPlanoChange(activated);
                    } catch (err) {
                      toast.error('Error al activar plano');
                    }
                  }} />
                  <div className="thumb-actions">
                    <button onClick={() => requestDeleteImage(p)}>Eliminar</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        {!modoCreacion ? (
          <div className="plano-crear">
            <span>Crear:</span>
            <button onClick={() => setModoCreacion('rectangulo')} className="btn-crear">
              ⬜ Rectángulo
            </button>
            <button onClick={() => setModoCreacion('circulo')} className="btn-crear">
              ⭕ Círculo
            </button>
            <button onClick={() => setModoCreacion('poligono')} className="btn-crear">
              🔷 Polígono
            </button>
          </div>
        ) : (
          <div className="plano-crear-activo">
            <span>
              Creando{' '}
              {modoCreacion === 'rectangulo'
                ? 'Rectángulo'
                : modoCreacion === 'circulo'
                  ? 'Círculo'
                  : 'Polígono'}
            </span>
            {modoCreacion === 'poligono' ? (
              <>
                <span className="hint">
                  Clic en el plano para agregar puntos ({puntosPoligono.length})
                </span>
                {puntosPoligono.length >= 3 && (
                  <button onClick={finalizarCreacion} className="btn-finalizar">
                    ✓ Finalizar ({puntosPoligono.length} puntos)
                  </button>
                )}
              </>
            ) : (
              <span className="hint">Arrastra en el plano para definir el tamaño</span>
            )}
            <button onClick={cancelarCreacion} className="btn-cancelar-crear">
              ✕ Cancelar
            </button>
          </div>
        )}

        {/* Controles de edición */}
        {loteEditando && (
          <div className="plano-editar-activo">
            <span>Editando: {loteEditando.numero}</span>
            {loteEditando.tipoForma === 'poligono' && (
              <div className="controles-poligono">
                <button
                  onClick={async () => {
                    const rect = canvasRef.current?.getBoundingClientRect();
                    if (rect) {
                      const coords = clientToImageCoords(rect.left + rect.width / 2, rect.top + rect.height / 2);
                      agregarPuntoPoligono(loteEditando, coords.x, coords.y);
                    }
                  }}
                  className="btn-agregar-punto"
                >
                  + Agregar Punto
                </button>
              </div>
            )}
            <button onClick={desactivarModoEdicion} className="btn-cerrar-edicion">
              ✕ Cerrar Edición
            </button>
          </div>
        )}

        <div className="plano-zoom">
          <button onClick={() => setScale(scale * 1.2)}>+</button>
          <span>{Math.round(scale * 100)}%</span>
          <button onClick={() => setScale(Math.max(0.5, scale * 0.8))}>-</button>
        </div>
      </div>
      <div className="plano-wrapper">
        <img
          ref={imageRef}
          src={currentPlanoUrl}
          alt="Plano de lotes"
          onLoad={handleImageLoad}
          style={{ display: 'none' }}
          onError={() => {
            console.warn('No se pudo cargar la imagen del plano. Usando canvas sin imagen.');
            setImageLoaded(true);
          }}
        />
        <canvas
          ref={canvasRef}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onMouseLeave={handleCanvasMouseUp} // Detener arrastre si el mouse sale del canvas
          onDoubleClick={(e) => {
            // Doble clic en un lote para editarlo
            if (modoCreacion) return;
            const rect = canvasRef.current?.getBoundingClientRect();
            if (!rect) return;
            const { x, y } = clientToImageCoords(e.clientX, e.clientY);
            const lote = obtenerLoteEnPosicion(x, y);
            if (lote) {
              activarModoEdicion(lote);
            }
          }}
          onClick={handleCanvasClick}
          className="plano-canvas"
          style={{
            cursor: isDragging
              ? handleEditando
                ? 'crosshair'
                : puntoEditando !== null
                  ? 'move'
                  : 'grabbing'
              : modoCreacion
                ? 'crosshair'
                : 'pointer',
          }}
        />
        {!imageLoaded && (
          <div className="plano-placeholder">
            <p>Plano de Lotes</p>
            <p className="plano-hint">
              Haz clic en un lote para ver detalles • Arrastra para mover • Doble clic para editar •
              Crea nuevas formas con los botones arriba
            </p>
          </div>
        )}

        {imageConfirm.visible && (
          <ConfirmModal
            visible={imageConfirm.visible}
            title="Eliminar imagen"
            message={`¿Estás seguro de que deseas eliminar la imagen ${imageConfirm.plano?.originalName || imageConfirm.plano?.filename || ''}?`}
            onConfirm={confirmDeleteImage}
            onCancel={cancelDeleteImage}
          />
        )}
      </div>
    </div>
  );
};

export default PlanoInteractivo;
